import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useEffectiveSupplierOrg, useMimic } from '../supplierOrganizations/MimicMode';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * What's new — the factory's side of the notification matrix, addressed to
 * the ORGANISATION.
 *
 * Kyu: "move notifications to organisation-level instead of person-level. I
 * do not want this feature blocked by missing Carnaval user accounts."
 *
 * Until 20261127000100 this strip read the signed-in account's own rows from
 * public.notifications — so a super admin previewing as Carnaval correctly
 * found none of their own and the strip hid itself, and Carnaval, having no
 * accounts at all, was written nothing in the first place. The news lives on
 * supplier_org_news now, one row per event per organisation, and this strip
 * carries the org explicitly like every other portal query.
 *
 * ── Deliberately a strip, not an Inbox ─────────────────────────────────────
 *
 * The portal is one page and a factory's news is one thread: their jobs.
 * A separate tab would put the news behind a click, which is the failure
 * this exists to fix. It renders nothing at all when there is nothing —
 * a permanently empty "Notifications" panel reads as something being broken.
 *
 * ── Read state is the organisation's ───────────────────────────────────────
 *
 * One person reading the strip reads it for the team — "did anyone at the
 * factory see this" is the real question. Mark all read is gated on
 * canWrite, so a mimicking super admin browsing does not silently consume
 * the factory's unread state; flipping on test writing makes it available,
 * same as every other portal write.
 *
 * ── What a row never carries ───────────────────────────────────────────────
 *
 * No TAP names — supplier_org_news has no actor column at all — no client
 * identity, no money. The sentences below speak the factory's vocabulary.
 */

interface PortalNewsRow {
  id: string;
  event_type: string;
  details: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

const PAGE = 12;

/** The kinds this surface knows how to say, in one place. */
const NEWS_KINDS = [
  'po_sent',
  'po_revised',
  'samples_decided',
  'revision_answered',
  'cg_status_changed',
  'sample_photos_uploaded',
] as const;

async function fetchOrgNews(
  client: SupabaseClient,
  organizationId: string,
): Promise<PortalNewsRow[]> {
  const { data, error } = await client
    .from('supplier_org_news')
    .select('id, event_type, details, read_at, created_at')
    .eq('supplier_organization_id', organizationId)
    .in('event_type', NEWS_KINDS)
    .order('read_at', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: false })
    .limit(PAGE);
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => ({
    ...row,
    details: (row.details ?? {}) as Record<string, unknown>,
  }));
}

function text(details: Record<string, unknown>, key: string): string {
  const value = details[key];
  return typeof value === 'string' ? value : '';
}

/**
 * One row, as a sentence in the factory's language.
 *
 * An unrecognised event still renders (de-underscored) rather than being
 * dropped — the same honesty rule as TAP's Inbox: a feed that hides what it
 * does not recognise lies by omission.
 */
export function portalSentence(row: Pick<PortalNewsRow, 'event_type' | 'details'>): string {
  const order = text(row.details, 'order_no');
  const name = order === '' ? 'a job' : `Job ${order}`;
  switch (row.event_type) {
    case 'po_sent':
      return `New job: TAP has sent you the PO for ${name}. Read it and accept, or ask for a change.`;
    case 'po_revised': {
      const reason = text(row.details, 'reason');
      return `The PO for ${name} was revised${
        reason === '' ? '' : ` — ${reason}`
      }. Your earlier acceptance no longer stands; please read the new sheet and accept again.`;
    }
    case 'samples_decided': {
      const note = text(row.details, 'note');
      const approved = text(row.details, 'decision') === 'approved';
      const verdict = approved
        ? `Your samples for ${name} are approved — build the full run.`
        : `TAP has asked for changes to your samples for ${name}.`;
      return note === '' ? verdict : `${verdict} "${note}"`;
    }
    case 'revision_answered':
      return `TAP has answered your question on ${name}. Open the job to read the outcome.`;
    case 'cg_status_changed': {
      const to = text(row.details, 'to');
      return to === '' ? `${name} moved to a new stage.` : `${name} moved to ${to}.`;
    }
    case 'sample_photos_uploaded':
      return `Sample photos for ${name} were added by your team.`;
    default:
      return `${row.event_type.replace(/_/g, ' ')} — ${name}`;
  }
}

export function PortalNotifications() {
  const { client, access } = useAuth();
  const { canWrite } = useMimic();
  const organizationId = useEffectiveSupplierOrg();
  const queryClient = useQueryClient();
  const rows = useQuery({
    // The org is in the key: a mimicking super admin switching factories must
    // never be served the previous one's cached news.
    queryKey: ['portal-news', organizationId],
    queryFn: () => fetchOrgNews(client, organizationId),
    enabled: access.status === 'approved' && organizationId !== '',
    retry: false,
  });
  const markAll = useMutation({
    mutationFn: async () => {
      const { error } = await client.rpc('mark_supplier_news_read', { p_org: organizationId });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['portal-news', organizationId] });
    },
  });

  const items = rows.data ?? [];
  const unread = items.filter((row) => row.read_at === null).length;

  // Silence over furniture: a brand-new factory with no news yet renders
  // nothing rather than an empty panel.
  if (rows.isPending || rows.isError || items.length === 0) {
    return null;
  }

  return (
    <section className="portal-news" aria-label="What's new">
      <div className="portal-news-head">
        <h3 className="portal-news-title">
          What&apos;s new
          {unread === 0 ? null : <Badge tone="brand">{unread} unread</Badge>}
        </h3>
        {unread === 0 || !canWrite ? null : (
          <Button
            variant="secondary"
            size="sm"
            disabled={markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            Mark all read
          </Button>
        )}
      </div>
      <ul className="portal-news-list">
        {items.map((row) => (
          <li
            key={row.id}
            className={row.read_at === null ? 'portal-news-row--unread' : 'portal-news-row'}
          >
            <span className="portal-news-date">{row.created_at.slice(0, 10)}</span>
            <span>{portalSentence(row)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
